import {
  buildSharedResourceScopeMenuItems,
  canManageSharedResource,
  hasSharedResourceCapability,
  normalizeSharedResourceScopeQuery,
  readSharedResourceScopeFromQuery,
  replaceSharedResourceScopeQuery,
  SHARED_RESOURCE_SCOPE_VALUES,
  type SharedResourceScope,
} from "@airange/shared";
import type { SharedResourceShareCapability } from "./shared-resource-share-form-adapter";

type SharedResourceReadableRecord<TAccessCapability extends string> = {
  owner_user_id?: number | string | null;
  share_summary: {
    current_user_capabilities: TAccessCapability[];
  };
};

function normalizeOwnerUserId(ownerUserId: number | string | null | undefined): number | null {
  if (ownerUserId === null || ownerUserId === undefined || ownerUserId === "") {
    return null;
  }
  const numericOwnerUserId = Number(ownerUserId);
  return Number.isFinite(numericOwnerUserId) ? numericOwnerUserId : null;
}

export type SharedResourceDescriptor = {
  resourceKey: string;
  scopeI18nPrefix: string;
};

export function createSharedResourceDescriptor<TAccessCapability extends string = SharedResourceShareCapability>(
  descriptor: SharedResourceDescriptor,
) {
  function readScopeFromQuery(canViewAllScope: boolean): SharedResourceScope {
    return readSharedResourceScopeFromQuery(canViewAllScope);
  }

  function replaceScopeQuery(scope: SharedResourceScope) {
    replaceSharedResourceScopeQuery(scope);
  }

  function normalizeScopeQuery(canViewAllScope: boolean): SharedResourceScope {
    return normalizeSharedResourceScopeQuery(canViewAllScope);
  }

  function buildScopeMenuItems(
    formatMessage: (value: { id: string }) => string,
    canViewAllScope: boolean,
  ) {
    return buildSharedResourceScopeMenuItems(
      (scope) => formatMessage({ id: `${descriptor.scopeI18nPrefix}.${scope}` }),
      canViewAllScope,
    );
  }

  function hasCapability(
    record: SharedResourceReadableRecord<TAccessCapability>,
    capability: TAccessCapability,
  ): boolean {
    return hasSharedResourceCapability(
      {
        owner_user_id: normalizeOwnerUserId(record.owner_user_id),
        share_summary: record.share_summary,
      },
      capability,
    );
  }

  function canClone(record: SharedResourceReadableRecord<TAccessCapability>): boolean {
    return hasCapability(record, "clone" as TAccessCapability);
  }

  function canEdit(
    record: SharedResourceReadableRecord<TAccessCapability>,
    actorUserId: number | null,
    isAdmin: boolean,
  ): boolean {
    return canManageSharedResource(
      { owner_user_id: normalizeOwnerUserId(record.owner_user_id) },
      actorUserId,
      isAdmin,
    );
  }

  function canManageSharing(
    record: SharedResourceReadableRecord<TAccessCapability>,
    actorUserId: number | null,
    isAdmin: boolean,
  ): boolean {
    return canManageSharedResource(
      { owner_user_id: normalizeOwnerUserId(record.owner_user_id) },
      actorUserId,
      isAdmin,
    );
  }

  return {
    ...descriptor,
    scopeValues: SHARED_RESOURCE_SCOPE_VALUES,
    readScopeFromQuery,
    replaceScopeQuery,
    normalizeScopeQuery,
    buildScopeMenuItems,
    hasCapability,
    canClone,
    canEdit,
    canManageSharing,
  };
}
